import { ModuleRegistry, AllCommunityModule } from "ag-grid-community";
import { ColumnMenuModule } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { useRef, useMemo } from "react";
import { useAtomValue } from "jotai";
import { showReadableLabelAtom } from "../../Labeling.state";
import { labelingTableTheme } from "./labelingTableTheme";
import { useDraft } from "../../hooks/useDraft";
import { useSelectedFile } from "../../hooks/useSelectedFile";
import { useRowData } from "../../hooks/useRowData";
import {
    AccountLabelCellRenderer,
    AccountLabelColDef,
    AccountNameCellRenderer,
    AccountNameColDef,
    PeriodColDef,
    RowHeaderCellRenderer,
    RowHeaderColDef,
} from "./columnDefinitions";
import { useRowSelection } from "../../hooks/useRowSelection";
import { AccountAppearanceState } from "../../Labeling.types";

ModuleRegistry.registerModules([AllCommunityModule, ColumnMenuModule]);

export function FileView() {
    const { draft, isReadonly } = useDraft();
    const { selectedFile, selectedFileId } = useSelectedFile();
    const gridRef = useRef<AgGridReact>(null);
    const showReadableLabel = useAtomValue(showReadableLabelAtom);
    const rowData = useRowData();
    const { onRowClicked } = useRowSelection();

    const columnDefs = useMemo(
        () => [
            {
                ...AccountLabelColDef,
                cellRenderer: (params) => (
                    <AccountLabelCellRenderer
                        label={params.data.account?.label}
                        showReadableLabel={showReadableLabel}
                        appearanceState={params.data.accountAppearanceState}
                        canChangeAccount={params.data.canChangeAccount}
                    />
                ),
            },
            {
                ...AccountNameColDef,
                cellRenderer: (params) => (
                    <AccountNameCellRenderer
                        accountName={params.data.account?.accountName}
                        appearanceState={params.data.accountAppearanceState}
                        canChangeAccount={params.data.canChangeAccount}
                    />
                ),
            },
            {
                ...RowHeaderColDef,
                cellRenderer: (params) => (
                    <RowHeaderCellRenderer
                        rowHeader={params.data.rowHeader}
                        appearanceState={params.data.accountAppearanceState}
                        canChangeAccount={params.data.canChangeAccount}
                    />
                ),
            },

            ...(draft.data?.periods?.map((period) =>
                PeriodColDef({ period }),
            ) || []),
        ],
        [draft.data?.periods, showReadableLabel],
    );

    if (!selectedFileId) return null;

    if (!draft.data && draft.isLoading)
        return (
            <div className="size-full">
                <div className="bg-selected size-full animate-pulse rounded-xl" />
            </div>
        );

    return (
        <div id="labeling-grid" className="size-full">
            <AgGridReact
                key={`${selectedFile?.id}-${isReadonly}-${draft.data?.id || "loading"}`}
                ref={gridRef}
                theme={labelingTableTheme}
                rowData={rowData}
                getRowId={(params) => params.data.rowIndex}
                onCellClicked={onRowClicked}
                columnDefs={columnDefs}
                defaultColDef={{
                    flex: 1,
                }}
                getRowClass={(params) => {
                    if (
                        params.data.accountAppearanceState ===
                        AccountAppearanceState.DISABLED
                    ) {
                        return "ag-row-disabled";
                    }
                }}
                rowSelection={{
                    mode: "multiRow",
                    checkboxes: false,
                    headerCheckbox: false,
                    enableClickSelection: true,
                }}
            />
        </div>
    );
}
